/**
 * Bold DOM readers + member price preview index for guests / membership-in-cart.
 * Bold injects live member prices only for tagged active members; everyone else
 * uses the exported variant index (Bold group rules) with ratio fallback.
 */
(function (global) {
  'use strict';

  var CURRENCY_PATTERN = /\$\s*[\d,]+(?:\.\d{2})?\s*(?:USD\b)?/gi;
  var MEMBER_TARGET = '[data-bold-price-target="member"]';
  var GUEST_SELECTOR = '[data-monthlees-guest-price], [data-bold-price-target="guest"]';
  var PRICING_ROOT = '[data-monthlees-card-pricing], [data-monthlees-price-ladder]';
  var SUBSCRIPTION_DISCOUNT = 0.9;

  var memberIndex = null;
  var memberIndexPromise = null;

  function getConfig() {
    return global.MonthleesMemberPricingConfig || {};
  }

  function parseAmount(value) {
    if (!value) return '';
    return String(value).replace(/[^0-9.]/g, '');
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var fmt = moneyFormat || getConfig().moneyFormat || '${{amount}}';
    if (global.Shopify && typeof global.Shopify.formatMoney === 'function') {
      return global.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function extractBoldMemberPrice(target) {
    if (!target) return null;

    var saleMoney = target.querySelector('ins .money, ins.money');
    if (saleMoney && saleMoney.textContent.trim()) {
      return saleMoney.textContent.trim();
    }

    var moneyEls = target.querySelectorAll('.money');
    if (moneyEls.length > 1) {
      var lastMoney = moneyEls[moneyEls.length - 1];
      if (lastMoney.textContent.trim()) {
        return lastMoney.textContent.trim();
      }
    }

    if (moneyEls.length === 1 && moneyEls[0].textContent.trim()) {
      return moneyEls[0].textContent.trim();
    }

    var text = target.textContent.trim();
    if (!text) return null;

    var matches = text.match(CURRENCY_PATTERN);
    if (matches && matches.length > 1) {
      return matches[matches.length - 1].trim();
    }

    if (matches && matches.length === 1) {
      return matches[0].trim();
    }

    return text;
  }

  function resolveBoldMemberDisplay(target, guestEl) {
    var memberPriceText = extractBoldMemberPrice(target);
    if (!memberPriceText || !guestEl) {
      return { boldApplied: false, text: null, cents: 0, source: null };
    }

    var memberAmount = parseAmount(memberPriceText);
    var guestAmount = parseAmount(guestEl.textContent || '');
    var boldApplied = memberAmount && guestAmount && memberAmount !== guestAmount;
    var cents = boldApplied ? Math.round(parseFloat(memberAmount) * 100) : 0;

    return {
      boldApplied: boldApplied,
      text: boldApplied ? memberPriceText : null,
      cents: cents,
      source: boldApplied ? 'bold' : null,
    };
  }

  function findPricingRoot(el) {
    return el && el.closest(PRICING_ROOT);
  }

  function resolveFromRoot(root) {
    if (!root) {
      return { boldApplied: false, text: null, cents: 0, source: null };
    }

    var target = root.querySelector(MEMBER_TARGET);
    var guest = root.querySelector(GUEST_SELECTOR);
    return resolveBoldMemberDisplay(target, guest);
  }

  function resolveFromTrigger(trigger) {
    return resolveFromRoot(findPricingRoot(trigger));
  }

  function loadMemberIndex() {
    if (memberIndex) {
      return Promise.resolve(memberIndex);
    }

    if (memberIndexPromise) {
      return memberIndexPromise;
    }

    var url = getConfig().memberPriceIndexUrl;
    if (!url) {
      memberIndex = {};
      return Promise.resolve(memberIndex);
    }

    memberIndexPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('member index fetch failed');
        return res.json();
      })
      .then(function (data) {
        memberIndex = data || {};
        return memberIndex;
      })
      .catch(function () {
        memberIndex = {};
        return memberIndex;
      });

    return memberIndexPromise;
  }

  function getIndexEntry(variantId) {
    if (!memberIndex || !variantId) return null;
    return memberIndex[String(variantId)] || memberIndex[variantId] || null;
  }

  function ratioMemberCents(guestCents) {
    var ratio = getConfig().memberRatio || 50;
    return Math.round((guestCents * ratio) / 100);
  }

  function subscriptionMemberCents(oneTimeMemberCents) {
    var percentOff = getConfig().subscriptionDiscountPercent;
    if (typeof percentOff === 'number' && percentOff > 0 && percentOff < 100) {
      return Math.round((oneTimeMemberCents * (100 - percentOff)) / 100);
    }
    return Math.round(oneTimeMemberCents * SUBSCRIPTION_DISCOUNT);
  }

  function lookupMemberCents(variantId, guestCents, options) {
    var opts = options || {};
    var entry = getIndexEntry(variantId);
    var oneTimeCents = 0;
    var source = null;

    // Priority: variant metafield (monthlees.member_price, rendered server-side by
    // Liquid) > fetched index > ratio estimate. The metafield is sourced from the
    // paired -member variant, needs no fetch, and cannot be staled by a theme push
    // the way the bundled index asset can.
    var mfCents = parseInt(opts.metafieldCents, 10) || 0;

    if (mfCents > 0) {
      oneTimeCents = mfCents;
      source = 'metafield';
    } else if (entry && entry.memberCents) {
      oneTimeCents = entry.memberCents;
      source = 'index';
    } else if (guestCents && opts.allowRatio !== false) {
      oneTimeCents = ratioMemberCents(guestCents);
      source = 'ratio';
    }

    if (!oneTimeCents) {
      return { cents: 0, oneTimeCents: 0, text: null, source: null };
    }

    var cents = opts.subscription ? subscriptionMemberCents(oneTimeCents) : oneTimeCents;
    var text = formatMoney(cents, opts.moneyFormat);
    if (opts.subscription) {
      text += '/mo';
    }

    return {
      cents: cents,
      oneTimeCents: oneTimeCents,
      text: text,
      source: source,
      boldApplied: true,
    };
  }

  function resolveMemberPreview(variantId, guestCents, options) {
    var opts = options || {};
    var lookup = lookupMemberCents(variantId, guestCents, opts);
    if (!lookup.cents) {
      return { boldApplied: false, text: null, cents: 0, source: null };
    }
    return lookup;
  }

  function resolveMemberPrice(triggerOrRoot, options) {
    var opts = options || {};
    var root =
      triggerOrRoot && triggerOrRoot.matches && triggerOrRoot.matches(PRICING_ROOT)
        ? triggerOrRoot
        : findPricingRoot(triggerOrRoot);

    var bold = resolveFromRoot(root);
    if (bold.boldApplied) {
      return bold;
    }

    var guest = root && root.querySelector(GUEST_SELECTOR);
    var variantId =
      opts.variantId ||
      (guest && guest.getAttribute('data-variant-id')) ||
      (triggerOrRoot && triggerOrRoot.getAttribute && triggerOrRoot.getAttribute('data-variant-id'));
    var guestCents =
      opts.guestCents ||
      (guest && parseInt(guest.getAttribute('data-guest-cents'), 10)) ||
      (triggerOrRoot && parseInt(triggerOrRoot.getAttribute('data-guest-cents'), 10)) ||
      0;

    if (opts.cartCents && opts.cartCents > 0 && opts.allowPreview !== false) {
      var cartText = formatMoney(opts.cartCents, opts.moneyFormat);
      if (opts.subscription) cartText += '/mo';
      return {
        boldApplied: true,
        text: cartText,
        cents: opts.cartCents,
        source: 'cart',
      };
    }

    if (opts.allowPreview === false) {
      return { boldApplied: false, text: null, cents: 0, source: null };
    }

    // Pick up the server-rendered metafield from the pricing root when the caller
    // has not passed it explicitly.
    var previewOpts = opts;
    if (!previewOpts.metafieldCents && root) {
      var rootMfCents = parseInt(root.getAttribute('data-member-price-cents'), 10) || 0;
      if (rootMfCents > 0) {
        previewOpts = {};
        for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) previewOpts[k] = opts[k];
        previewOpts.metafieldCents = rootMfCents;
      }
    }

    return resolveMemberPreview(variantId, guestCents, previewOpts);
  }

  function primeMemberIndex() {
    return loadMemberIndex();
  }

  global.MonthleesBoldPricing = {
    parseAmount: parseAmount,
    formatMoney: formatMoney,
    extractBoldMemberPrice: extractBoldMemberPrice,
    resolveBoldMemberDisplay: resolveBoldMemberDisplay,
    findPricingRoot: findPricingRoot,
    resolveFromRoot: resolveFromRoot,
    resolveFromTrigger: resolveFromTrigger,
    loadMemberIndex: loadMemberIndex,
    primeMemberIndex: primeMemberIndex,
    lookupMemberCents: lookupMemberCents,
    resolveMemberPreview: resolveMemberPreview,
    resolveMemberPrice: resolveMemberPrice,
    subscriptionMemberCents: subscriptionMemberCents,
  };

  primeMemberIndex();
})(window);
